import * as Sentry from "@sentry/react"
import { env } from "configs/EnvironmentConfig"

import { addAxiosRequestContext } from "./axiosRequestContext"

export const SENTRY_SESSION_KEY = "sentry_sid"

export const initializeSentry = (): void => {
  const dsn = import.meta.env.VITE_SENTRY_FRONTEND_DSN
  if (!dsn) {
    console.warn("Couldn't initialize Sentry: DSN is not provided")
    return
  }

  // Don't enable Sentry during tests to reduce noise
  if (import.meta.env.VITE_DISABLE_SENTRY === "true") {
    return
  }

  sessionStorage[SENTRY_SESSION_KEY] ??= crypto.randomUUID()

  Sentry.init({
    environment: env.ENVIRONMENT_NAME,
    dsn: import.meta.env.VITE_SENTRY_FRONTEND_DSN,

    // SDK v11 collects HTTP bodies, cookies, DB query data and stack-frame variables by default,
    // which would send patient data to Sentry
    dataCollection: {
      userInfo: true,
      cookies: false,
      httpHeaders: { request: true, response: true },
      httpBodies: [],
      urlQueryParams: true,
      graphQL: { document: true, variables: true },
      genAI: { inputs: true, outputs: true },
      databaseQueryData: false,
      queues: true,
      stackFrameVariables: false,
      frameContextLines: 5,
    },
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Avoid using webworker as our CSP rules prevent this
        useCompression: false,
      }),
      Sentry.captureConsoleIntegration({
        levels: ["error"],
      }),
    ],

    /**
     * Discards events whose resolvable frame filenames all point somewhere other than
     * a script file: runtime-evaluated code reports the document URL as its source, so
     * its frames carry a route path rather than a bundle URL.
     *
     * For example, an event whose frames all sit at `/register` or `/z/brainport/` is
     * discarded, while one with a frame at `/assets/index-C1AddgHf.js` is kept.
     *
     * events with no resolvable filename are kept.
     *
     * Events caused by an axios error get the request's host and endpoint, see `addAxiosRequestContext`.
     */
    beforeSend: (event, hint) => {
      const frames = event.exception?.values?.flatMap(({ stacktrace }) => stacktrace?.frames ?? []) ?? []
      const filenames = frames.map(({ filename }) => filename).filter((f) => f !== undefined)

      if (filenames.length > 0 && !filenames.some((f) => /\.m?js(?:[?#]|$)/.test(f))) {
        return null
      }

      return addAxiosRequestContext(event, hint.originalException)
    },

    tracesSampleRate: 0.1,
    tracePropagationTargets: [
      // By default sentry only does trace propagation to the the same origin
      // This is needed to capture traces from our apis
      /^https:\/\/api\.(staging\.)?mijnpraktijk\.nl\/api\//,
    ],

    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  })

  Sentry.setUser({ id: sessionStorage[SENTRY_SESSION_KEY] })
}
